import { Plus, Search } from "lucide-react";
import {
	Button,
	Input,
	Modal,
	Notification,
	Table,
	TextArea,
	useNotification,
} from "../../components/FormComponents";
import Pagination from "../../components/Pagination";
import { useCrudPage } from "../../hooks/useCrudPage";
import { companySchema } from "../../schemas/companySchema";

export default function Companies() {
	const { notification, showNotification } = useNotification();

	const {
		data: companies,
		loading,
		saving,
		page,
		limit,
		total,
		search,
		isModalOpen,
		editingId,
		formData,
		errors,
		setPage,
		setSearch,
		updateField,
		handleOpenModal,
		handleCloseModal,
		handleSubmit,
		handleDelete,
	} = useCrudPage("/companies", companySchema, {
		onSuccess: (action) => {
			const messages = {
				created: "Empresa criada com sucesso!",
				updated: "Empresa atualizada com sucesso!",
				deleted: "Empresa deletada com sucesso!",
			};
			showNotification(messages[action]);
		},
		defaultFormData: {
			name: "",
			legal_name: "",
			informal_name: "",
			cnpj: "",
			email: "",
			phone: "",
			site: "",
			logo_url: "",
			tagline: "",
			description: "",
		},
	});

	const columns = [
		{ key: "name", label: "Nome" },
		{ key: "legal_name", label: "Razão Social" },
		{ key: "cnpj", label: "CNPJ" },
		{ key: "email", label: "Email" },
	];

	return (
		<div className="space-y-6">
			<Notification notification={notification} />

			{/* Header */}
			<div className="flex items-center justify-between">
				<h1 className="text-2xl font-bold text-white">Gerenciar Empresas</h1>
				<Button type="button" onClick={() => handleOpenModal()}>
					<Plus className="w-4 h-4 inline mr-2" />
					Nova Empresa
				</Button>
			</div>

			{/* Search */}
			<div className="relative">
				<Search
					className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400"
					aria-hidden="true"
				/>
				<input
					type="text"
					value={search}
					onChange={(e) => {
						setSearch(e.target.value);
						setPage(1);
					}}
					placeholder="Buscar empresas..."
					aria-label="Buscar empresas"
					className="w-full pl-10 pr-4 py-2.5 bg-slate-900/50 border border-white/10 rounded-lg text-slate-300 placeholder-slate-500 focus:outline-none focus:border-cyan-500/50 focus:ring-2 focus:ring-cyan-500/30 transition-all"
				/>
			</div>

			{/* Table */}
			<div className="bg-slate-900/50 backdrop-blur border border-white/5 rounded-xl p-6">
				<Table
					columns={columns}
					data={companies}
					loading={loading}
					onEdit={handleOpenModal}
					onDelete={(id) =>
						handleDelete(
							id,
							"Tem certeza que deseja deletar esta empresa? Isso afetará todas as propostas associadas.",
						)
					}
				/>

				{/* Pagination Component Profissional */}
				<Pagination
					page={page}
					limit={limit}
					total={total}
					onPageChange={setPage}
				/>
			</div>

			{/* Form Modal */}
			<Modal
				isOpen={isModalOpen}
				title={editingId ? "Editar Empresa" : "Nova Empresa"}
				onClose={handleCloseModal}
				onSubmit={handleSubmit}
				submitLabel={editingId ? "Atualizar" : "Criar"}
				loading={saving}
			>
				<div className="space-y-4">
					<Input
						label="Nome *"
						value={formData.name || ""}
						onChange={(e) => updateField("name", e.target.value)}
						placeholder="Nome fantasia"
						error={errors.name}
					/>
					<Input
						label="Razão Social *"
						value={formData.legal_name || ""}
						onChange={(e) => updateField("legal_name", e.target.value)}
						placeholder="Razão social completa"
						error={errors.legal_name}
					/>
					<Input
						label="Nome Informal"
						value={formData.informal_name || ""}
						onChange={(e) => updateField("informal_name", e.target.value)}
						placeholder="Apelido ou acrônimo"
					/>
					<Input
						label="CNPJ"
						value={formData.cnpj || ""}
						onChange={(e) => updateField("cnpj", e.target.value)}
						placeholder="00.000.000/0000-00"
					/>
					<Input
						label="Email"
						type="email"
						value={formData.email || ""}
						onChange={(e) => updateField("email", e.target.value)}
						placeholder="contato@empresa.com"
						error={errors.email}
					/>
					<Input
						label="Telefone"
						value={formData.phone || ""}
						onChange={(e) => updateField("phone", e.target.value)}
						placeholder="+55 (11) 9999-9999"
					/>
					<Input
						label="Site"
						value={formData.site || ""}
						onChange={(e) => updateField("site", e.target.value)}
						placeholder="https://empresa.com"
					/>
					<Input
						label="URL do Logo"
						value={formData.logo_url || ""}
						onChange={(e) => updateField("logo_url", e.target.value)}
						placeholder="https://..."
					/>
					<Input
						label="Tagline"
						value={formData.tagline || ""}
						onChange={(e) => updateField("tagline", e.target.value)}
						placeholder="Ex: DevOps & Cloud Engineering"
					/>
					<TextArea
						label="Descricao da Empresa"
						value={formData.description || ""}
						onChange={(e) => updateField("description", e.target.value)}
						placeholder="Breve descricao da empresa para exibir nas propostas"
						rows="3"
					/>
				</div>
			</Modal>
		</div>
	);
}
